import React, { useState, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { FinanceTable } from './FinanceTable';
import { FinanceSummaryCards } from './FinanceSummaryCards';
import { FinanceTableFilters } from './FinanceTableFilters';
import { useFinanceListColumns } from '../../hooks/useFinanceListColumns';
import { FinanceFilters, calcFinanceFiltersAmount } from 'modules/finance/filters';
import { Nullable } from 'global';
import { FilterEvent } from 'components/shared/Filters';

export const FinanceList = () => {
  const { columns } = useFinanceListColumns();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [currentFilterConfig, setCurrentFilterConfig] = useState<Nullable<Partial<FinanceFilters>>>(null);

  // State for aggregate metrics from the API
  const [aggregatedMetrics, setAggregatedMetrics] = useState({
    totalAftermarketProfit: 0,
    totalFIProfit: 0,
    productsSoldCount: 0,
  });

  const onTableFiltersChanged = useCallback((filters: Partial<FinanceFilters>, event: FilterEvent) => {
    if (event.type === 'apply') {
      setCurrentFilterConfig(filters);
    }
    if (event.type === 'reset') {
      setCurrentFilterConfig(null);
    }
  }, []);

  const handleMetricsChange = useCallback(
    (newMetrics: {
      productsSoldCount: number;
      totalAftermarketProfit: number;
      totalFinanceInsuranceProfit: number;
    }) => {
      setAggregatedMetrics({
        productsSoldCount: newMetrics.productsSoldCount,
        totalAftermarketProfit: newMetrics.totalAftermarketProfit,
        totalFIProfit: newMetrics.totalFinanceInsuranceProfit,
      });
    },
    []
  );

  const filtersAmount = calcFinanceFiltersAmount(currentFilterConfig);

  return (
    <Box>
      <FinanceSummaryCards
        totalAftermarketProfit={aggregatedMetrics.totalAftermarketProfit}
        totalFIProfit={aggregatedMetrics.totalFIProfit}
        productsSoldCount={aggregatedMetrics.productsSoldCount}
      />

      <Box mb={2}>
        <Button variant="outlined" startIcon={<FilterList />} onClick={() => setIsFiltersOpen(true)}>
          Filters {filtersAmount > 0 && `(${filtersAmount})`}
        </Button>
      </Box>

      <FinanceTable
        columns={columns}
        currentFilterConfig={currentFilterConfig}
        onAggregatedMetricsChange={handleMetricsChange}
      />

      <FinanceTableFilters
        isFiltersOpen={isFiltersOpen}
        currentFilterConfig={currentFilterConfig}
        setIsFiltersOpen={setIsFiltersOpen}
        onTableFiltersChanged={onTableFiltersChanged}
      />
    </Box>
  );
};
